import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  LoaderCircle,
  Mail,
  MessageCircle,
  RefreshCw,
  Send,
  UserRound,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  clearChatGuestIdentity,
  getMyChatConversation,
  identifyChatGuest,
  markMyChatRead,
  receiveCustomerChatMessage,
  resendChatVerification,
  sendMyChatMessage,
  startChatConversation,
  updateCustomerChatConversation,
} from "../../actions/chatActions";
import {
  joinChatConversation,
  resetChatSocket,
  subscribeChatSocket,
} from "../../utils/chatSocket";
import "./CustomerChatWidget.css";

const ENQUIRY_OPTIONS = [
  { value: "costume_hire", label: "Costume Hire" },
  { value: "bespoke_tailoring", label: "Bespoke Tailoring" },
  { value: "made_to_measure", label: "Made to Measure" },
  { value: "textiles", label: "Textiles" },
  { value: "vintage_reproductions", label: "Vintage Reproductions" },
  { value: "jewellery", label: "Jewellery" },
  { value: "haberdashery", label: "Haberdashery" },
  { value: "millinery", label: "Millinery" },
  { value: "production_film_theatre", label: "Production / Film / Theatre" },
  {
    value: "existing_order_or_appointment",
    label: "Existing Order or Appointment",
  },
  { value: "other", label: "Other" },
];

function safeText(value = "") {
  return String(value || "").trim();
}

function getId(value = {}) {
  if (value && typeof value === "object") {
    return safeText(value.id || value._id);
  }

  return safeText(value);
}

function formatTime(value = "") {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return "";
  }
}

function getUserId(userInfo = null) {
  return getId(userInfo);
}

function firstNameFrom(value = "") {
  return safeText(value).split(/\s+/)[0] || "";
}

function isStaffRole(role = "") {
  return ["admin", "super_admin", "store_manager"].includes(
    safeText(role).toLowerCase(),
  );
}

function isCustomerMessage(message = {}) {
  return !isStaffRole(message.senderRole);
}

function normalizeComparable(value = "") {
  return safeText(value).replace(/\s+/g, " ").toLowerCase();
}

function isMeaningfulInitialMessage(value = "") {
  const comparable = normalizeComparable(value);

  return comparable.length > 2 && !["hi", "hey", "yo"].includes(comparable);
}

function enquiryLabel(value = "") {
  return (
    ENQUIRY_OPTIONS.find((option) => option.value === safeText(value))?.label ||
    ""
  );
}

function buildProductContext({ location, productBySlug, productDetails }) {
  if (!location.pathname.includes("/product/")) return {};

  const product = productBySlug?.product || productDetails?.product || null;
  const productId = getId(product);
  const productName = safeText(product?.name || product?.title);

  if (!productId && !productName) return {};

  return {
    productId,
    productName,
  };
}

export default function CustomerChatWidget() {
  const dispatch = useDispatch();
  const location = useLocation();
  const messageEndRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [enquiryType, setEnquiryType] = useState("");
  const [draft, setDraft] = useState("");
  const [localError, setLocalError] = useState("");
  const [notice, setNotice] = useState("");
  const [failedMessage, setFailedMessage] = useState("");
  const [messageStatus, setMessageStatus] = useState("");

  const {
    loading = false,
    sending = false,
    identifying = false,
    resending = false,
    clearingIdentity = false,
    conversation = null,
    messages = [],
    error = null,
  } = useSelector((state) => state.customerChat || {});
  const {
    userInfo = null,
    isAuthenticated = false,
    checked: authChecked = false,
    loading: authLoading = false,
  } = useSelector((state) => state.userAuth || {});
  const productBySlug = useSelector((state) => state.productBySlug || {});
  const productDetails = useSelector((state) => state.productDetails || {});

  const userId = getUserId(userInfo);
  const conversationId = getId(conversation);
  const isAdminPath = location.pathname.startsWith("/admin");
  const isSignedInCustomer = Boolean(isAuthenticated && userId);
  const isVerifiedRegisteredCustomer =
    isSignedInCustomer && userInfo?.isEmailVerified === true;
  const isCheckingChatGate = !authChecked || authLoading;
  const unreadCount = Number(conversation?.unreadCustomerCount || 0);
  const hasCustomerMessages = messages.some(isCustomerMessage);
  const verifiedEmail = Boolean(
    isVerifiedRegisteredCustomer || conversation?.emailVerified,
  );
  const currentFirstName =
    safeText(conversation?.firstName) ||
    firstNameFrom(conversation?.customerName) ||
    firstNameFrom(name) ||
    firstNameFrom(userInfo?.name);
  const currentEmail = safeText(conversation?.customerEmail) || safeText(email);
  const needsIdentity =
    !isCheckingChatGate && !isSignedInCustomer && !conversationId;
  const needsVerification =
    !isCheckingChatGate &&
    ((isSignedInCustomer && !isVerifiedRegisteredCustomer) ||
      (!isSignedInCustomer && Boolean(conversationId) && !verifiedEmail));
  const needsEnquiryGate =
    !isCheckingChatGate && verifiedEmail && (!conversationId || !hasCustomerMessages);
  const busy = sending || identifying || resending || clearingIdentity;

  const productContext = useMemo(
    () => buildProductContext({ location, productBySlug, productDetails }),
    [location, productBySlug, productDetails],
  );

  useEffect(() => {
    resetChatSocket();
  }, [userId]);

  useEffect(() => {
    if (isAdminPath) return;

    dispatch(getMyChatConversation()).catch(() => {});
  }, [dispatch, isAdminPath, userId]);

  useEffect(() => {
    if (isAdminPath) return undefined;

    return subscribeChatSocket({
      onMessage: (payload) => {
        dispatch(receiveCustomerChatMessage(payload));
      },
      onConversationUpdate: (payload) => {
        dispatch(updateCustomerChatConversation(payload));
      },
    });
  }, [dispatch, isAdminPath]);

  useEffect(() => {
    if (!conversationId) return;

    joinChatConversation(conversationId).catch(() => {});
  }, [conversationId]);

  useEffect(() => {
    if (!open || !conversationId || unreadCount <= 0) return;

    dispatch(markMyChatRead(conversationId)).catch(() => {});
  }, [conversationId, dispatch, open, unreadCount]);

  useEffect(() => {
    if (!open) return;

    messageEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, open]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get("chat") !== "verified") return;

    setOpen(true);
    setNotice("Email verified. You can start your chat.");
    dispatch(getMyChatConversation()).catch(() => {});
  }, [dispatch, location.search]);

  useEffect(() => {
    if (!isSignedInCustomer || !userInfo) return;

    setName(safeText(userInfo.name));
    setEmail(safeText(userInfo.email));
  }, [isSignedInCustomer, userInfo]);

  useEffect(() => {
    if (!conversation) return;

    if (conversation.firstName || conversation.customerName) {
      setName(safeText(conversation.firstName || conversation.customerName));
    }

    if (conversation.customerEmail) {
      setEmail(safeText(conversation.customerEmail));
    }

    if (conversation.enquiryType) {
      setEnquiryType(safeText(conversation.enquiryType));
    }
  }, [conversation]);

  useEffect(() => {
    if (messageStatus !== "sent") return undefined;

    const timer = window.setTimeout(() => setMessageStatus(""), 2400);

    return () => window.clearTimeout(timer);
  }, [messageStatus]);

  if (isAdminPath) {
    return null;
  }

  const handleIdentitySubmit = async (event) => {
    event.preventDefault();
    setLocalError("");
    setNotice("");

    try {
      await dispatch(
        identifyChatGuest({
          firstName: firstNameFrom(name) || name,
          email,
          product: productContext,
          website: honeypot,
        }),
      );
      setNotice("Check your email for the verification link.");
    } catch (submitError) {
      setLocalError(
        submitError?.normalizedMessage ||
          submitError?.message ||
          "We could not send your verification email.",
      );
    }
  };

  const sendChatMessage = async (messageText = "", { initial = false } = {}) => {
    const safeMessage = safeText(messageText);

    if (!safeMessage) return;

    if (initial && !enquiryType) {
      setLocalError("Choose an enquiry type before starting chat.");
      return;
    }

    if (initial && !isMeaningfulInitialMessage(safeMessage)) {
      setLocalError("Tell us briefly what you are looking for before starting chat.");
      return;
    }

    setLocalError("");
    setFailedMessage("");
    setMessageStatus("sending");

    try {
      if (conversationId && !initial) {
        await dispatch(sendMyChatMessage(conversationId, safeMessage));
      } else {
        await dispatch(
          startChatConversation({
            name: safeText(name) || safeText(userInfo?.name),
            firstName: currentFirstName,
            email: safeText(email) || safeText(userInfo?.email),
            enquiryType,
            message: safeMessage,
            product: productContext,
          }),
        );
      }

      setDraft("");
      setMessageStatus("sent");
    } catch (submitError) {
      setFailedMessage(safeMessage);
      setMessageStatus("failed");
      setLocalError(
        submitError?.normalizedMessage ||
          submitError?.message ||
          "We could not send that message.",
      );
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await sendChatMessage(draft);
  };

  const handleInitialSubmit = async (event) => {
    event.preventDefault();
    await sendChatMessage(draft, { initial: true });
  };

  const handleResend = async () => {
    setLocalError("");
    setNotice("");

    try {
      await dispatch(resendChatVerification());
      setNotice("Verification email sent again.");
    } catch (resendError) {
      setLocalError(
        resendError?.normalizedMessage ||
          resendError?.message ||
          "We could not resend your verification email.",
      );
    }
  };

  const handleClearIdentity = async () => {
    setLocalError("");
    setNotice("");

    try {
      await dispatch(clearChatGuestIdentity());
      setName("");
      setEmail("");
      setHoneypot("");
      setEnquiryType("");
      setDraft("");
      setFailedMessage("");
    } catch (clearError) {
      setLocalError(
        clearError?.normalizedMessage ||
          clearError?.message ||
          "We could not change your chat details.",
      );
    }
  };

  const canIdentify =
    safeText(name) && safeText(email) && !identifying && !clearingIdentity;
  const canStart =
    safeText(draft) && enquiryType && !sending && !identifying && !resending;
  const canSend = safeText(draft) && conversationId && !sending;
  const resendCooldown = Number(conversation?.resendCooldownSeconds || 0);
  const headerStatus = currentFirstName
    ? `Welcome back, ${currentFirstName}`
    : "Academy Costumes London";

  const renderFeedback = () =>
    localError || error || notice || messageStatus ? (
      <div
        className={`customer-chat-feedback${
          localError || error || messageStatus === "failed" ? " is-error" : ""
        }`}
      >
        {localError ||
          error ||
          (messageStatus === "sending"
            ? "Sending..."
            : messageStatus === "sent"
              ? "Sent"
              : notice)}
        {failedMessage ? (
          <button
            type="button"
            className="customer-chat-inline-action"
            onClick={() =>
              sendChatMessage(failedMessage, { initial: needsEnquiryGate })
            }
            disabled={busy}
          >
            Retry
          </button>
        ) : null}
      </div>
    ) : null;

  const renderMessages = () => (
    <div className="customer-chat-body">
      {messages.length ? (
        messages.map((message) => {
          const staff = isStaffRole(message.senderRole);

          return (
            <article
              key={getId(message)}
              className={`customer-chat-message${
                staff ? " from-staff" : " from-customer"
              }`}
            >
              <div className="customer-chat-message-text">{message.message}</div>
              <div className="customer-chat-message-meta">
                {staff ? "Academy Costumes London" : "You"}
                {formatTime(message.createdAt)
                  ? ` | ${formatTime(message.createdAt)}`
                  : ""}
              </div>
            </article>
          );
        })
      ) : (
        <div className="customer-chat-empty">
          Send us a message and we will reply here.
        </div>
      )}
      <div ref={messageEndRef} />
    </div>
  );

  const renderIdentityScreen = () => (
    <form className="customer-chat-screen" onSubmit={handleIdentitySubmit}>
      <div className="customer-chat-screen-icon">
        <UserRound size={22} strokeWidth={1.8} />
      </div>
      <h3>Chat with Academy Costumes London</h3>
      <p>Tell us who we should reply to, then verify your email to begin.</p>

      <label>
        First name
        <input
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoComplete="given-name"
          maxLength={80}
          required
        />
      </label>

      <label>
        Email
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          maxLength={254}
          required
        />
      </label>

      <label className="customer-chat-honeypot" aria-hidden="true">
        Company
        <input
          type="text"
          value={honeypot}
          onChange={(event) => setHoneypot(event.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </label>

      {renderFeedback()}

      <button
        type="submit"
        className="customer-chat-primary"
        disabled={!canIdentify}
      >
        {identifying ? (
          <LoaderCircle
            className="customer-chat-loader"
            size={18}
            strokeWidth={1.8}
          />
        ) : null}
        Continue
      </button>
    </form>
  );

  const renderVerificationScreen = () => (
    <div className="customer-chat-screen">
      <div className="customer-chat-screen-icon">
        <Mail size={22} strokeWidth={1.8} />
      </div>
      <h3>
        {isSignedInCustomer ? "Verify your account email" : "Check your email"}
      </h3>
      <p>
        {isSignedInCustomer
          ? "Please verify your account email before starting chat."
          : `We sent a verification link to ${
              currentEmail || "your email address"
            }.`}
      </p>

      {currentFirstName || currentEmail ? (
        <div className="customer-chat-identity-summary">
          {currentFirstName ? <strong>{currentFirstName}</strong> : null}
          {currentEmail ? <span>{currentEmail}</span> : null}
        </div>
      ) : null}

      {renderFeedback()}

      {!isSignedInCustomer ? (
        <>
          <button
            type="button"
            className="customer-chat-primary"
            onClick={handleResend}
            disabled={resending || resendCooldown > 0}
          >
            {resending ? (
              <LoaderCircle
                className="customer-chat-loader"
                size={18}
                strokeWidth={1.8}
              />
            ) : (
              <RefreshCw size={18} strokeWidth={1.8} />
            )}
            {resendCooldown > 0
              ? `Resend in ${resendCooldown}s`
              : "Resend verification"}
          </button>

          <button
            type="button"
            className="customer-chat-secondary"
            onClick={handleClearIdentity}
            disabled={clearingIdentity}
          >
            <ArrowLeft size={17} strokeWidth={1.8} />
            Change email
          </button>
        </>
      ) : null}
    </div>
  );

  const renderEnquiryScreen = () => (
    <form className="customer-chat-screen" onSubmit={handleInitialSubmit}>
      <div className="customer-chat-screen-icon is-verified">
        <CheckCircle2 size={22} strokeWidth={1.8} />
      </div>
      <h3>
        {currentFirstName ? `Welcome back, ${currentFirstName}` : "How can we help?"}
      </h3>
      <p>Choose the closest enquiry type and send your first message.</p>

      <div className="customer-chat-options" role="group" aria-label="Enquiry type">
        {ENQUIRY_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`customer-chat-option${
              enquiryType === option.value ? " is-selected" : ""
            }`}
            onClick={() => setEnquiryType(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {enquiryType ? (
        <div className="customer-chat-selected">
          {enquiryLabel(enquiryType)}
        </div>
      ) : null}

      <textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Tell us what you are looking for"
        rows={4}
        maxLength={2000}
        required
      />

      {renderFeedback()}

      <button
        type="submit"
        className="customer-chat-primary"
        disabled={!canStart}
      >
        {sending ? (
          <LoaderCircle
            className="customer-chat-loader"
            size={18}
            strokeWidth={1.8}
          />
        ) : (
          <Send size={18} strokeWidth={1.8} />
        )}
        Start chat
      </button>

      {!isSignedInCustomer ? (
        <button
          type="button"
          className="customer-chat-secondary"
          onClick={handleClearIdentity}
          disabled={clearingIdentity}
        >
          Not you?
        </button>
      ) : null}
    </form>
  );

  return (
    <section
      className={`customer-chat-widget${open ? " is-open" : ""}`}
      aria-label="Customer chat"
    >
      {open ? (
        <div className="customer-chat-panel">
          <header className="customer-chat-header">
            <div>
              <div className="customer-chat-title">Chat with us</div>
              <div className="customer-chat-status">{headerStatus}</div>
            </div>

            <button
              type="button"
              className="customer-chat-icon-button"
              onClick={() => setOpen(false)}
              aria-label="Minimise chat"
              title="Minimise chat"
            >
              <ChevronDown size={20} strokeWidth={1.8} />
            </button>
          </header>

          {isCheckingChatGate || (loading && !conversation) ? (
            <div className="customer-chat-body">
              <div className="customer-chat-empty">Loading chat...</div>
            </div>
          ) : needsIdentity ? (
            renderIdentityScreen()
          ) : needsVerification ? (
            renderVerificationScreen()
          ) : needsEnquiryGate ? (
            renderEnquiryScreen()
          ) : (
            <>
              {renderMessages()}

              <form className="customer-chat-compose" onSubmit={handleSubmit}>
                {renderFeedback()}

                <div className="customer-chat-input-row">
                  <textarea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder="Type your message"
                    rows={2}
                    maxLength={2000}
                    required
                  />
                  <button
                    type="submit"
                    className="customer-chat-send"
                    disabled={!canSend}
                    aria-label="Send message"
                    title="Send message"
                  >
                    {sending ? (
                      <LoaderCircle
                        className="customer-chat-loader"
                        size={18}
                        strokeWidth={1.8}
                      />
                    ) : (
                      <Send size={18} strokeWidth={1.8} />
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      ) : null}

      <button
        type="button"
        className="customer-chat-launcher"
        onClick={() => setOpen(true)}
        aria-label="Open chat"
        title="Open chat"
      >
        <MessageCircle size={24} strokeWidth={1.8} />
        {unreadCount > 0 ? (
          <span className="customer-chat-badge">{unreadCount}</span>
        ) : null}
      </button>
    </section>
  );
}
